import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { FlexContainer } from '@/ui/styled-ui';
import { showItSupport, SupportIcon } from '@/utils/helpers';
import { getFirstNamePart } from '@/utils/helpers/display.helper';
import { isMobile } from '@/utils/helpers/ui.helper';
import { Accordion, AccordionTab } from 'primereact/accordion';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledContainer = styled(FlexContainer)<{
  hasLogo?: boolean;
  hasContact?: boolean;
}>`
  grid-area: cb;
  display: grid;
  background-color: var(--purple-dark);
  border-radius: var(--default-border-radius);
  color: var(--white-main);
  padding: ${({ hasContact }) =>
    hasContact ? 'var(--small-padding)' : 'var(--default-padding)'};
  gap: var(--default-padding);
  align-items: stretch;
  grid-template-columns: repeat(6, minmax(auto, 1fr));
  grid-template-areas: ${({ hasContact }) =>
    hasContact
      ? '"info info info info contact contact"'
      : '"info info info info info info"'};

  @media (max-width: 1439px) {
    grid-template-columns: repeat(4, minmax(auto, 1fr));
    grid-template-areas: ${({ hasContact }) =>
      hasContact ? '"info info contact contact"' : '"info info info info"'};
  }

  @media (max-width: 799px) {
    grid-template-columns: repeat(2, minmax(auto, 1fr));
    grid-template-areas: ${({ hasContact }) =>
      hasContact ? '"info info"' + '"contact contact"' : '"info info"'};
  }

  &.learner {
    margin-bottom: var(--default-padding);
  }

  .customer-logo {
    grid-area: logo;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: var(--small-padding);
    background-color: var(--white-main);
    border-radius: var(--small-border-radius);
    min-height: 132px;
    min-width: 132px;
    max-height: 132px;
    max-width: 132px;
    width: 132px;
    height: 132px;
    margin-right: var(--default-padding);
  }

  .customer-info {
    grid-area: info;
    padding-left: ${({ hasLogo }) =>
      !hasLogo ? 'var(--default-padding)' : '0'};
    h2 {
      line-height: var(--heading-medium-line-height);
      font-size: var(--heading-medium-font-size);
      margin-block: 0px;
    }
    p {
      line-height: var(--xsmall-line-height);
      font-size: var(--xsmall-font-size);
      margin-block: 0px;
    }
  }

  .mobile-customer-info {
    grid-area: info;
    gap: var(--small-padding);
    h2 {
      line-height: var(--heading-small-line-height);
      font-size: var(--heading-small-font-size);
      margin-block: 0px;
      margin-bottom: var(--small-padding);
    }
    p {
      line-height: var(--xsmall-line-height);
      font-size: var(--xsmall-font-size);
      margin-block: 0px;
    }
    .customer-logo {
      margin-right: 0px;
    }
  }

  .contact-info {
    grid-area: contact;
    background-color: var(--purple-middle);
    border-radius: var(--small-border-radius);
    position: relative;
    align-items: flex-start;
    justify-content: flex-start;
    height: 100%;
    padding: var(--medium-padding);

    svg {
      position: absolute;
      top: var(--medium-padding);
      right: var(--medium-padding);
    }

    h3 {
      font-size: var(--medium-font-size);
      line-height: var(--medium-line-height);
      margin: 0px;
    }
    h6 {
      font-size: var(--xsmall-font-size);
      line-height: var(--xsmall-line-height);
      font-weight: 600;
      margin: 0px;
      margin-bottom: var(--small-padding);
      span {
        font-weight: 400;
      }
    }

    .contact-info-row {
      font-size: var(--xsmall-font-size);
      line-height: var(--xsmall-line-height);
      align-items: center;
      justify-content: flex-start;
      gap: var(--xsmall-padding);

      .pi {
        color: var(--purple-light);
      }
    }
  }
`;

const StyledAccordion = styled(Accordion)`
  &.p-accordion {
    grid-area: contact;
    width: 100%;
    background-color: var(--purple-middle);
    border-radius: var(--small-border-radius);
    padding: var(--medium-padding);

    .p-accordion-header {
      width: 100%;
      display: inline-block;
      .p-accordion-header-link {
        display: flex;
        justify-content: space-between;
        color: var(--white-main);
        font-weight: 500;
        background-color: transparent;
        border: none;
        padding: 0;
        .accordion-header {
          color: var(--white-main);
        }

        &:not(.p-disabled):hover {
          color: var(--white-main);
          background-color: transparent;
        }
      }

      .p-accordion-toggle-icon {
        order: 1;
        padding-left: var(--small-padding);
      }

      &:not(.p-disabled).p-highlight .p-accordion-header-link {
        background-color: transparent;
        color: var(--white-main);
      }
      &:not(.p-disabled) .p-accordion-header-link:focus {
        box-shadow: none;
      }
      &:not(.p-disabled).p-highlight:hover {
        .p-accordion-header-link {
          background-color: transparent;
          border: none;
        }
      }

      &:not(p-disabled):hover {
        background-color: transparent;
      }
    }

    .p-accordion-content {
      position: relative;
      border: none;
      padding: 0;
      color: var(--white-main);
      background-color: transparent;
      .p-calendar .p-inputtext:not(.p-dropdown-label) {
        padding-left: var(--xsmall-padding);
      }
    }
    &.p-accordion-header:not(.p-disabled).p-highlight .p-accordion-header-link {
      background-color: transparent;
    }

    h3 {
      margin: 0px;
      font-size: var(--medium-font-size);
      line-height: var(--medium-line-height);
    }

    svg {
      position: absolute;
      bottom: 0;
      right: 0;
    }

    h6 {
      align-self: flex-start;
      font-size: var(--xsmall-font-size);
      line-height: var(--xsmall-line-height);
      font-weight: 600;
      margin: 0px;
      margin-top: var(--xxsmall-padding);
      margin-bottom: var(--small-padding);
      span {
        font-weight: 400;
      }
    }

    .contact-info-row {
      font-size: var(--xsmall-font-size);
      line-height: var(--xsmall-line-height);
      align-items: center;
      justify-content: flex-start;
      gap: var(--xsmall-padding);

      .pi {
        color: var(--purple-light);
      }
    }
  }
`;

const StyledImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const CustomerBranding: React.FC<{ isLearner?: boolean }> = ({
  isLearner,
}) => {
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const { t } = useTranslation();

  const hasContactInfo = showItSupport(currentAccount?.branding);

  return (
    <StyledContainer
      justify="space-between"
      align="flex-start"
      className={isLearner ? 'learner' : 'admin'}
      wrap={isMobile() ? 'wrap' : 'no-wrap'}
      hasLogo={!!currentAccount?.companyLogo}
      hasContact={!!hasContactInfo}
    >
      <FlexContainer
        className={isMobile() ? 'mobile-customer-info' : 'customer-info'}
      >
        {!isMobile() && !!currentAccount?.companyLogo && (
          <div className="customer-logo">
            <StyledImg
              src={currentAccount?.companyLogo?.signedUrl}
              alt={currentAccount?.platformSettings?.name}
            />
          </div>
        )}
        <FlexContainer direction="column" align="flex-start" justify="center">
          <h2>
            {t('account.branding.welcome', {
              userName: getFirstNamePart(currentUser?.firstName),
            })}
          </h2>
          <p>
            {t('account.branding.description', {
              accountName: currentAccount?.name,
            })}
          </p>
        </FlexContainer>
        {isMobile() && !!currentAccount?.companyLogo && (
          <div className="customer-logo">
            <StyledImg
              src={currentAccount?.companyLogo?.signedUrl}
              alt={currentAccount?.platformSettings?.name}
            />
          </div>
        )}
      </FlexContainer>
      {hasContactInfo && (
        <>
          {isMobile() ? (
            <StyledAccordion
              collapseIcon="pi pi-chevron-up"
              expandIcon="pi pi-chevron-down"
            >
              <AccordionTab
                headerTemplate={
                  <h3>
                    {currentAccount?.branding?.itSupportSectionTitle ||
                      t('account.it.supportContact')}
                  </h3>
                }
              >
                <FlexContainer direction="column">
                  {(currentAccount?.branding?.itSupportName ||
                    currentAccount?.branding?.itSupportTitle) && (
                    <h6>
                      {currentAccount?.branding?.itSupportName
                        ? currentAccount.branding.itSupportName
                        : ''}
                      {currentAccount?.branding?.itSupportTitle && (
                        <span>{`, ${currentAccount.branding.itSupportTitle}`}</span>
                      )}
                    </h6>
                  )}
                  {currentAccount?.branding?.itSupportEmail && (
                    <FlexContainer className="contact-info-row mb-2">
                      <span className="pi pi-at" />
                      <span>{currentAccount.branding.itSupportEmail}</span>
                    </FlexContainer>
                  )}
                  {currentAccount?.branding?.itSupportPhone && (
                    <FlexContainer className="contact-info-row">
                      <span className="pi pi-phone" />
                      <span>+{currentAccount.branding.itSupportPhone}</span>
                    </FlexContainer>
                  )}
                  <SupportIcon />
                </FlexContainer>
              </AccordionTab>
            </StyledAccordion>
          ) : (
            <FlexContainer direction="column" className="contact-info">
              <SupportIcon />
              <h3>
                {currentAccount?.branding?.itSupportSectionTitle ||
                  t('account.it.supportContact')}
              </h3>
              {(currentAccount?.branding?.itSupportName ||
                currentAccount?.branding?.itSupportTitle) && (
                <h6>
                  {currentAccount?.branding?.itSupportName
                    ? currentAccount.branding.itSupportName
                    : ''}
                  {currentAccount?.branding?.itSupportTitle && (
                    <span>{`, ${currentAccount.branding.itSupportTitle}`}</span>
                  )}
                </h6>
              )}
              {currentAccount?.branding?.itSupportEmail && (
                <FlexContainer className="contact-info-row mb-2">
                  <span className="pi pi-at" />
                  <span>{currentAccount.branding.itSupportEmail}</span>
                </FlexContainer>
              )}
              {currentAccount?.branding?.itSupportPhone && (
                <FlexContainer className="contact-info-row">
                  <span className="pi pi-phone" />
                  <span>+{currentAccount.branding.itSupportPhone}</span>
                </FlexContainer>
              )}
            </FlexContainer>
          )}
        </>
      )}
    </StyledContainer>
  );
};
